'use client';

import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import { ButtonLink, type ButtonVariant } from '@commons/components/Button';
import { Link } from '@commons/i18n/navigation';
import GatedInsightDrawer from '@commons/pagebuilder/webforms/GatedInsightDrawer';
import type { DrupalLink } from '@commons/types/drupal';

// Type definition for shared gated webinar action properties.

type BaseProps = {
  alias?: string;
  link?: DrupalLink;
  isGated?: boolean;
  ctaLabel?: string;
};

type WebinarNodeLinkProps = BaseProps & {
  children: ReactElement;
  className?: string;
};

/**
 * Wraps a webinar card-like display with either a direct link or gated drawer trigger.
 */

export const WebinarNodeLink = (props: WebinarNodeLinkProps) => {
  const { alias, link, isGated, className, children } = props;

  if (!link) return children;

  if (isGated && alias) {
    return (
      <Link href={alias} target="_self" className={className}>
        {children}
      </Link>
    );
  }

  const renderedLink = (
    <Link href={link.url} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </Link>
  );

  if (!isGated) return renderedLink;

  return (
    <GatedInsightDrawer
      url={link.url}
      fallback={renderedLink}
      trigger={children}
      openURLOnSuccess
    />
  );
};

type WebinarNodeButtonLinkProps = BaseProps & {
  variant?: ButtonVariant;
  disabled?: boolean;
};

/**
 * Renders a webinar CTA as either a direct link button or gated drawer trigger.
 */

export const WebinarNodeButtonLink = (props: WebinarNodeButtonLinkProps) => {
  const t = useTranslations('commons');
  const { alias, link, isGated, ctaLabel, variant = 'primary', disabled } = props;

  const label = ctaLabel || t('learnMore');
  const pageURL = isGated ? alias : undefined;
  const buttonURL = pageURL || link?.url || '#';

  const renderedButtonLink = (
    <ButtonLink
      href={buttonURL}
      variant={variant}
      disabled={(!link && !alias) || disabled}
      {...(isGated && alias
        ? { target: '_self' }
        : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {label}
    </ButtonLink>
  );

  if (isGated && alias) return renderedButtonLink;
  if (!link || !isGated) return renderedButtonLink;

  return (
    <GatedInsightDrawer
      url={link.url}
      fallback={renderedButtonLink}
      triggerProps={{ label, variant, disabled }}
      openURLOnSuccess
    />
  );
};
